// ── School ↔ Lieferant identifier API client ────────────────────────────────
// Backed by /api/school-supplier-identifiers (SCD Type 2).
//
// Schema: functions/intranet/sql/017_dim_school_supplier_identifier.sql
// Backend: functions/intranet/src/functions/schoolSupplierIdentifiers.js
//
// SCD2 supersede flow: a customer-number change closes the previous open
// row (sets valid_to = day before new valid_from) AND inserts a fresh row
// — never overwrites in place. Typo corrections use PUT /{id}.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'
import type { SchoolSupplierIdentifier } from './types'

const KEY = ['identifiers'] as const

// Pass a schoolId to load ONLY that school's identifiers (server-side filter) —
// the whole-table pull is slow. Mutations invalidate ['identifiers'] which
// prefix-matches the scoped key, so cache invalidation still works.
export function useIdentifiers(schoolId?: string) {
  return useQuery({
    queryKey: schoolId ? [...KEY, schoolId] : KEY,
    queryFn: () => api.get<SchoolSupplierIdentifier[]>(
      schoolId
        ? `/api/school-supplier-identifiers?schoolId=${encodeURIComponent(schoolId)}`
        : '/api/school-supplier-identifiers',
    ),
    staleTime: 60_000,
  })
}

// Eigener Lieferanten-Schlüssel; bestehende Mutationen invalidieren weiter KEY.
export function useOpenIdentifiersByLieferant(lieferantId?: string) {
  return useQuery({
    queryKey: [...KEY, 'lieferant', lieferantId, 'open'],
    queryFn: () => api.get<SchoolSupplierIdentifier[]>(
      `/api/school-supplier-identifiers?lieferantId=${encodeURIComponent(lieferantId!)}&open=true`,
    ),
    enabled: !!lieferantId,
    staleTime: 60_000,
  })
}

export interface IdentifierUpsertInput {
  id?: string                // optional — if provided, update existing in place
  schoolId: string
  lieferantId: string
  customerNumber: string
  validFrom: string          // ISO date
  validTo: string | null
  notes: string | null
  /**
   * If true and creating a new entry, the backend automatically closes any
   * currently-open row for the same (school, lieferant) by setting its
   * valid_to to the day before the new valid_from. Standard SCD2 supersede.
   * Ignored on update.
   */
  closeCurrent?: boolean
}

export function useUpsertIdentifier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: IdentifierUpsertInput): Promise<SchoolSupplierIdentifier> => {
      if (input.id) {
        // PUT — typo fix / amend notes on an existing row.
        return api.put<SchoolSupplierIdentifier>(
          `/api/school-supplier-identifiers/${encodeURIComponent(input.id)}`,
          {
            schoolId: input.schoolId,
            lieferantId: input.lieferantId,
            customerNumber: input.customerNumber,
            validFrom: input.validFrom,
            validTo: input.validTo,
            notes: input.notes,
          },
        )
      }
      // POST — create new (with optional SCD2 supersede).
      return api.post<SchoolSupplierIdentifier>(
        '/api/school-supplier-identifiers',
        {
          schoolId: input.schoolId,
          lieferantId: input.lieferantId,
          customerNumber: input.customerNumber,
          validFrom: input.validFrom,
          validTo: input.validTo,
          notes: input.notes,
          closeCurrent: input.closeCurrent ?? false,
          source: 'manual',
        },
      )
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteIdentifier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: true }>(
        `/api/school-supplier-identifiers/${encodeURIComponent(id)}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useCloseIdentifier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, validTo }: { id: string; validTo: string }) =>
      api.patch<SchoolSupplierIdentifier>(
        `/api/school-supplier-identifiers/${encodeURIComponent(id)}/close`,
        { validTo },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
